"use client";

import { useId, useState, useEffect, useTransition, useCallback, useMemo } from "react";
import { useActionState } from "react";
import { submitContact } from "../../lib/submitContact";


// ✅ Static hotels (shown only for "business")
const HOTELS = [
  { id: "dottingen", name: "Hotel Döttingen" },
  { id: "bern", name: "Hotel Bern" },
];

const initialState = undefined as
  | {
      ok: boolean;
      fieldErrors?: Record<string, string[]>;
      formErrors?: string[];
      message?: string;
    }
  | undefined;

export default function ContactForm() {
  const [audience, setAudience] = useState<"individual" | "business">(
    "individual",
  );
  const [state, formAction] = useActionState(submitContact, initialState);
  const [isPending, startTransition] = useTransition();
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  // auto-focus first error
  useEffect(() => {
    if (!state?.fieldErrors) return;
    const firstKey = Object.keys(state.fieldErrors)[0];
    const el = document.querySelector<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >(`[name="${firstKey}"]`);
    el?.focus();
  }, [state]);


  // Client-side validation - memoized for performance
  const validateForm = useCallback((
    formData: FormData,
    currentAudience: "individual" | "business",
  ) => {
    const errors: Record<string, string> = {};

    // Required fields for all
    const name = formData.get("name")?.toString().trim();
    if (!name) {
      errors.name = "Name is required";
    }

    const email = formData.get("email")?.toString().trim();
    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    const message = formData.get("message")?.toString().trim();
    if (!message) {
      errors.message = "Message is required";
    }

    // Business-specific validation
    if (currentAudience === "business") {
      const hotel = formData.get("hotel")?.toString();
      if (!hotel || hotel.trim() === "" || hotel === "Select a hotel") {
        errors.hotel = "Hotel selection is required";
      }

      const guests = formData.get("guests")?.toString();
      if (!guests || guests.trim() === "" || guests === "Enter a number") {
        errors.guests = "Number of guests is required";
      } else {
        const guestNum = parseInt(guests.trim());
        if (isNaN(guestNum) || guestNum < 1 || guestNum > 6) {
          errors.guests = "Please enter a valid number of guests (1-6)";
        }
      }
    }

    return errors;
  }, []);

  const handleSubmit = (formData: FormData) => {
    const errors = validateForm(formData, audience);
    setClientErrors(errors);

    if (Object.keys(errors).length === 0) {
      setClientErrors({});
      startTransition(() => formAction(formData));
    }
  };

  return (
    <section className="grid md:grid-cols-2 max-w-7xl mx-auto relative gap-20">
      {/* Left form */}
      <div className="py-16">
        <h1 className="text-5xl md:text-6xl font-light font-noto">
          Got questions?
          <br />
          We're here to help.
        </h1>

        {/* Tabs */}
        <div className="mt-10 border-b border-gray-200 flex gap-8">
          <button
            type="button"
            onClick={() => setAudience("individual")}
            className={`pb-3 border-b-2 ${
              audience === "individual"
                ? "border-black"
                : "border-transparent text-gray-500"
            }`}
          >
            For Individuals
          </button>
          <button
            type="button"
            onClick={() => setAudience("business")}
            className={`pb-3 border-b-2 ${
              audience === "business"
                ? "border-black"
                : "border-transparent text-gray-500"
            }`}
          >
            For businesses
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            handleSubmit(formData);
          }}
          className="mt-8 space-y-6 max-w-xl"
        >
          <input type="hidden" name="audience" value={audience} />

          {/* Honeypot field - hidden from users but visible to bots */}
          <div style={{ display: "none" }} aria-hidden="true">
            <label htmlFor="website">Website (leave empty)</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* General validation error */}
          {Object.keys(clientErrors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <p className="text-red-800 text-sm font-medium">
                Please fix the following errors:
              </p>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {Object.values(clientErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <Field
              name="name"
              label="Full name"
              placeholder="Enter your full name"
              errors={
                state?.fieldErrors?.name ||
                (clientErrors.name ? [clientErrors.name] : undefined)
              }
              required
            />
            <Field
              name="email"
              label="Email"
              type="email"
              placeholder="Enter your email"
              errors={
                state?.fieldErrors?.email ||
                (clientErrors.email ? [clientErrors.email] : undefined)
              }
              required
            />
          </div>

          {/* Business fields - always render to prevent layout shift */}
          <div className="grid md:grid-cols-2 gap-6 min-h-[120px]">
            <div className={`transition-opacity duration-300 ${
              audience === "business" 
                ? "opacity-100" 
                : "opacity-0"
            }`}>
              <SelectField
                name="hotel"
                label="Hotel"
                placeholder="Select a hotel"
                options={HOTELS}
                errors={
                  state?.fieldErrors?.hotel ||
                  (clientErrors.hotel ? [clientErrors.hotel] : undefined)
                }
                required
              />
            </div>
            <div className={`transition-opacity duration-300 ${
              audience === "business" 
                ? "opacity-100" 
                : "opacity-0"
            }`}>
              <Field
                name="guests"
                label="Number of guests"
                type="number"
                placeholder="Enter a number"
                min={1}
                max={6}
                inputMode="numeric"
                errors={
                  state?.fieldErrors?.guests ||
                  (clientErrors.guests ? [clientErrors.guests] : undefined)
                }
                required
              />
            </div>
          </div>

          <TextArea
            name="message"
            label="Message"
            placeholder="Enter your message here..."
            errors={
              state?.fieldErrors?.message ||
              (clientErrors.message ? [clientErrors.message] : undefined)
            }
            required
          />


          <button
            type="submit"
            disabled={isPending}
            className="relative cursor-pointer flex justify-center min-w-28 z-10 xl:py-3 xl:px-4 py-2.5 px-3 flex items-center gap-2 text-sm uppercase bg-primary text-white w-fit transition rounded-4xl"
          >
            {isPending ? "Submitting..." : "Submit"}
          </button>

          {state?.formErrors?.length ? (
            <div className="text-red-700">{state.formErrors[0]}</div>
          ) : null}
          {state?.ok && state.message ? (
            <div className="text-green-700">{state.message}</div>
          ) : null}
        </form>
      </div>

      {/* Right image */}
      <div className="hidden md:block relative">
        <img
          src="/assets/6I9A4355.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  errors,
  className,
  required = false,
  ...rest
}: {
  label: string;
  name: string;
  errors?: string[];
  className?: string;
  required?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        id={id}
        name={name}
        {...rest}
        className="w-full border-b border-gray-200 py-2 focus:outline-none"
        aria-invalid={!!errors?.length}
        required={required}
      />
      {errors?.length ? (
        <p className="text-sm text-red-600">{errors[0]}</p>
      ) : null}
    </div>
  );
}

function SelectField({
  label,
  name,
  placeholder,
  options,
  errors,
  required = false,
}: {
  label: string;
  name: string;
  placeholder?: string;
  options: { id: string; name: string }[];
  errors?: string[];
  required?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        id={id}
        name={name}
        className="w-full border-b py-2 focus:outline-none border-gray-200"
        aria-invalid={!!errors?.length}
        required={required}
        defaultValue=""
      >
        <option value="" disabled>
          {placeholder || "Select an option"}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.name}>
            {o.name}
          </option>
        ))}
      </select>
      {errors?.length ? (
        <p className="text-sm text-red-600">{errors[0]}</p>
      ) : null}
    </div>
  );
}

function TextArea({
  label,
  name,
  errors,
  className,
  required = false,
  ...rest
}: {
  label: string;
  name: string;
  errors?: string[];
  className?: string;
  required?: boolean;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <textarea
        id={id}
        name={name}
        rows={5}
        {...rest}
        className="w-full border-b py-2 focus:outline-none border-gray-200"
        aria-invalid={!!errors?.length}
        required={required}
      />
      {errors?.length ? (
        <p className="text-sm text-red-600">{errors[0]}</p>
      ) : null}
    </div>
  );
}
